import { useAppSSE } from 'contexts/AppSSEContext';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { airportsApi } from 'services/weather/airports/airports.api';
import { fireDangerApi } from 'services/weather/fireDanger/fireDanger.api';
import { marineApi } from 'services/weather/marine/marine.api';
import { weatherApi } from 'services/weather/weather.api';

export default function useCustomWeatherEvents({ enabled = true } = {}) {
  const { subscribe } = useAppSSE();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!enabled) return;

    const handlers = {
      'cw-current-conditions-finished': () => {
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'CURRENT' }]));
        dispatch(airportsApi.util.invalidateTags([{ type: 'AirportWeather', id: 'CONDITIONS' }]));
      },
      'cw-buoy-report-finished': () =>
        dispatch(marineApi.util.invalidateTags([{ type: 'MarineWeather', id: 'BUOY' }])),
      'cw-hourly-forecast-48hr-finished': () =>
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'HOURLY_48' }])),
      'cw-hourly-forecast-finished': () =>
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'HOURLY_7_DAY' }])),
      'cw-detailed-fire-danger-finished': () =>
        dispatch(
          fireDangerApi.util.invalidateTags([{ type: 'FireDangerWeather', id: 'DETAILED' }]),
        ),
      'cw-extended-fire-danger-finished': () =>
        dispatch(
          fireDangerApi.util.invalidateTags([{ type: 'FireDangerWeather', id: 'EXTENDED' }]),
        ),
      'cw-detailed-forecast-5day-finished': () =>
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'DETAILED' }])),
      'cw-airport-delay-finished': () =>
        dispatch(airportsApi.util.invalidateTags([{ type: 'AirportWeather', id: 'DELAYS' }])),
      'cw-15day-expanded-forecast-finished': () =>
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'DAY_15' }])),
      'cw-detailed-marine-7day-finished': () =>
        dispatch(marineApi.util.invalidateTags([{ type: 'MarineWeather', id: 'DETAILED_7_DAY' }])),
      'cw-astronomy-forecast-ext-finished': () =>
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'ASTRONOMY' }])),
      'cw-tide-forecast-finished': () =>
        dispatch(marineApi.util.invalidateTags([{ type: 'MarineWeather', id: 'TIDE' }])),
      // No separate extended-marine query exists in the frontend; refresh the active marine views.
      'cw-one-extended-marine-finished': () =>
        dispatch(marineApi.util.invalidateTags(['MarineWeather'])),
      'cw-air-quality-obs-finished': () =>
        dispatch(
          weatherApi.util.invalidateTags([{ type: 'Weather', id: 'AIR_QUALITY_OBSERVATION' }]),
        ),
      'cw-air-quality-for-finished': () =>
        dispatch(weatherApi.util.invalidateTags([{ type: 'Weather', id: 'AIR_QUALITY_FORECAST' }])),
    };

    const unsubscribers = Object.entries(handlers).map(([eventType, handler]) =>
      subscribe(eventType, handler),
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [enabled, subscribe, dispatch]);
}
